import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Loader2, ShieldCheck, Eye, EyeOff, AlertCircle, CheckCircle2 } from 'lucide-react';
import { Button } from '@shared/components/ui/button';
import { Input } from '@shared/components/ui/input';
import { Label } from '@shared/components/ui/label';
import { Alert, AlertDescription } from '@shared/components/ui/alert';
import { supabase } from '@shared/supabaseClient';
import { ROUTES } from '@shared/config/routes';

/**
 * ResetPasswordPage
 *
 * Handles Supabase password-reset / invite email links.
 * Supabase appends #access_token=...&type=recovery (or type=invite)
 * to the redirectTo URL. The Supabase JS client with detectSessionInUrl:true
 * automatically picks up those tokens and creates a session, which we then
 * use to let the user set their new password.
 */
const Layout = ({ children }) => (
  <div className="min-h-screen flex">
    <div
      className="w-full lg:w-1/3 flex items-center justify-center p-8"
      style={{
        backgroundColor: '#0f0f0f',
        backgroundImage: `
          repeating-linear-gradient(45deg, transparent, transparent 10px, rgba(255,255,255,.02) 10px, rgba(255,255,255,.02) 20px),
          repeating-linear-gradient(-45deg, transparent, transparent 10px, rgba(255,255,255,.02) 10px, rgba(255,255,255,.02) 20px)
        `,
      }}
    >
      <div className="w-full max-w-md">
        <div className="mb-8 flex justify-center">
          <img src="/assets/Atlaslogo.png" alt="Logo" className="h-28 w-auto" />
        </div>
        {children}
      </div>
    </div>
    <div
      className="hidden lg:flex lg:w-2/3 relative items-end p-12"
      style={{
        backgroundImage: `linear-gradient(rgba(0,0,0,0.45),rgba(0,0,0,0.45)), url('/assets/ATC-PHOTO-71.jpg')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
    >
      <div className="text-white">
        <h2 className="text-4xl font-bold mb-4">Site Management, Made Simple</h2>
        <p className="text-lg text-gray-200 mb-4">
          Track visitor inductions, manage site check-ins and check-outs, and ensure compliance — all in one place.
        </p>
        <p className="text-sm text-gray-400">© 2025 Depot Induction System · Powered by Apexiar</p>
      </div>
    </div>
  </div>
);

const ResetPasswordPage = () => {
  const navigate = useNavigate();

  const [password, setPassword]               = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword]       = useState(false);
  const [showConfirm, setShowConfirm]         = useState(false);
  const [loading, setLoading]                 = useState(false);
  const [checking, setChecking]               = useState(true);
  const [error, setError]                     = useState(null);
  const [done, setDone]                       = useState(false);
  const [sessionReady, setSessionReady]       = useState(false);

  // Wait for Supabase to process the URL hash tokens and fire PASSWORD_RECOVERY
  useEffect(() => {
    // Check if there's already an active session (Supabase may have already processed the hash)
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) {
        setSessionReady(true);
        setChecking(false);
      }
    });

    // Listen for the PASSWORD_RECOVERY event emitted when Supabase detects the URL hash
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'PASSWORD_RECOVERY' || event === 'SIGNED_IN') {
        if (session) {
          setSessionReady(true);
          setChecking(false);
        }
      }
    });

    // Fallback: if no event fires within 3 s the link is probably expired/invalid
    const fallback = setTimeout(() => {
      setChecking(false);
    }, 3000);

    return () => {
      subscription.unsubscribe();
      clearTimeout(fallback);
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setLoading(true);
    try {
      const { error: updateError } = await supabase.auth.updateUser({ password });
      if (updateError) throw updateError;
      setDone(true);
      // Redirect to login after a short delay
      setTimeout(() => navigate(ROUTES.AUTH.LOGIN, { replace: true }), 3000);
    } catch (err) {
      setError(err.message || 'Failed to update password. The link may have expired.');
    } finally {
      setLoading(false);
    }
  };

  // ── Password set successfully ─────────────────────────────────────
  if (done) {
    return (
      <Layout>
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="text-center">
          <div className="mx-auto mb-6 w-16 h-16 rounded-full bg-green-600 flex items-center justify-center">
            <CheckCircle2 className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-3">Password set!</h1>
          <p className="text-gray-400 mb-6">
            Your password has been updated. You'll be redirected to the login page shortly.
          </p>
          <Button
            onClick={() => navigate(ROUTES.AUTH.LOGIN, { replace: true })}
            className="border-2"
            style={{ backgroundColor: 'hsl(45 100% 50%)', color: 'hsl(0 0% 10%)', borderColor: 'hsl(45 100% 50%)' }}
          >
            Go to Login
          </Button>
        </motion.div>
      </Layout>
    );
  }

  // ── Checking for session ──────────────────────────────────────────
  if (checking) {
    return (
      <Layout>
        <div className="flex flex-col items-center gap-4 text-gray-400">
          <Loader2 className="w-8 h-8 animate-spin" />
          <p>Verifying your link…</p>
        </div>
      </Layout>
    );
  }

  // ── No valid session — link expired or already used ───────────────
  if (!sessionReady) {
    return (
      <Layout>
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="text-center">
          <div className="mx-auto mb-6 w-16 h-16 rounded-full bg-red-600 flex items-center justify-center">
            <AlertCircle className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-3">Link expired or invalid</h1>
          <p className="text-gray-400 mb-6">
            This password-reset link has expired or has already been used.<br />
            Please request a new one from the login page.
          </p>
          <Button
            onClick={() => navigate(ROUTES.AUTH.LOGIN, { replace: true })}
            className="border-2"
            style={{ backgroundColor: 'hsl(45 100% 50%)', color: 'hsl(0 0% 10%)', borderColor: 'hsl(45 100% 50%)' }}
          >
            Back to Login
          </Button>
        </motion.div>
      </Layout>
    );
  }

  // ── Set new password form ─────────────────────────────────────────
  return (
    <Layout>
      <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.4 }}>
        <div className="mb-8 text-center">
          <div className="mx-auto mb-4 w-16 h-16 rounded-full bg-yellow-500 flex items-center justify-center">
            <ShieldCheck className="w-8 h-8 text-black" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">Set Your Password</h1>
          <p className="text-gray-400">Choose a strong password to secure your account.</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <div className="space-y-2">
            <Label htmlFor="password" className="text-gray-300 text-base">New Password</Label>
            <div className="relative">
              <Input
                id="password"
                type={showPassword ? 'text' : 'password'}
                placeholder="Min. 8 characters"
                value={password}
                onChange={e => setPassword(e.target.value)}
                required
                disabled={loading}
                className="h-14 bg-gray-800 border-2 text-white placeholder:text-gray-500 text-base pr-12"
                style={{ borderColor: 'hsl(45 100% 50%)' }}
              />
              <button
                type="button"
                onClick={() => setShowPassword(p => !p)}
                tabIndex={-1}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
              >
                {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              </button>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="confirm" className="text-gray-300 text-base">Confirm Password</Label>
            <div className="relative">
              <Input
                id="confirm"
                type={showConfirm ? 'text' : 'password'}
                placeholder="Re-enter your password"
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                required
                disabled={loading}
                className="h-14 bg-gray-800 border-2 text-white placeholder:text-gray-500 text-base pr-12"
                style={{ borderColor: 'hsl(45 100% 50%)' }}
              />
              <button
                type="button"
                onClick={() => setShowConfirm(p => !p)}
                tabIndex={-1}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
              >
                {showConfirm ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              </button>
            </div>
          </div>

          <Button
            type="submit"
            disabled={loading}
            className="w-full h-14 text-lg font-semibold border-2"
            style={{ backgroundColor: 'hsl(45 100% 50%)', color: 'hsl(0 0% 10%)', borderColor: 'hsl(45 100% 50%)' }}
          >
            {loading ? <><Loader2 className="mr-2 h-6 w-6 animate-spin" />Saving…</> : 'Set Password & Sign In'}
          </Button>
        </form>
      </motion.div>
    </Layout>
  );
};

export default ResetPasswordPage;
